/**
 * Iter S · Stage 3 — Read-only Email Viewer modal.
 *
 * Shows the immutable snapshot captured at send time (subject, rendered
 * HTML/text, recipient, delivery status, timestamps, provider message id).
 * The HTML is rendered inside a hardened iframe:
 *
 *   - `sandbox=""` — no scripts, no forms, no top-navigation, no popups.
 *   - `srcdoc` — no cross-origin loads. Doc is served entirely inline.
 *   - Inline `Content-Security-Policy: default-src 'none'` — belt-and-braces
 *     block on remote loads even if the sandbox regressed in a future
 *     browser bug.
 *   - Images are stripped by default so tracking pixels don't fire when
 *     the admin opens the preview. A "Show images" toggle re-enables them
 *     with an explicit user action.
 *
 * Historical rows with no stored HTML render a clear "content was not
 * stored" message — never a reconstructed template.
 */
import React, { useEffect, useState, useMemo } from "react";
import { X, Loader2, Image as ImageIcon, EyeOff, ClipboardCopy, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { detailLong } from "@/lib/businessTime";


const STATUS_TONE = {
  sent:                   "bg-emerald-100 text-emerald-900 border-emerald-800",
  failed:                 "bg-red-100 text-red-900 border-red-800",
  not_configured:         "bg-amber-100 text-amber-900 border-amber-800",
  partial:                "bg-amber-100 text-amber-900 border-amber-800",
  skipped_disabled:       "bg-neutral-100 text-neutral-700 border-neutral-500",
  skipped_not_configured: "bg-neutral-100 text-neutral-700 border-neutral-500",
};


/** Strip <img> tags to defuse tracking pixels. Called for the default preview. */
function stripImages(html) {
  if (!html) return html;
  // Also neutralize inline background images just in case.
  return html
    .replace(/<img\b[^>]*>/gi, '<!-- img blocked by preview -->')
    .replace(/style="([^"]*)"/gi, (_m, s) => `style="${s.replace(/background(?:-image)?\s*:\s*url\([^)]*\)/gi, "background:none")}"`);
}


/**
 * Wrap the delivered HTML in a strict shell so we can enforce CSP inside
 * the iframe. The `default-src 'none'` policy blocks scripts, images,
 * fonts, media, iframes — everything. When the admin opts to "Show
 * images" we relax the CSP just for `img-src` (still blocking scripts).
 */
function buildSrcdoc(html, allowImages) {
  const csp = allowImages
    ? "default-src 'none'; img-src * data:; style-src 'unsafe-inline';"
    : "default-src 'none'; style-src 'unsafe-inline';";
  const safe = allowImages ? html : stripImages(html);
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8">
    <meta http-equiv="Content-Security-Policy" content="${csp}">
    <meta name="referrer" content="no-referrer">
    <style>
      body { font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color:#111; margin:16px; }
      a { pointer-events: none; text-decoration: underline; color:#0057b7; }
    </style>
  </head>
  <body>${safe}</body>
</html>`;
}


function copyToClipboard(value) {
  if (!value) return;
  try {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value);
    } else {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.top = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
  } catch (_e) { /* no-op */ }
}


export default function EmailViewerModal({ providerMessageId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [showImages, setShowImages] = useState(false);
  const [showText, setShowText] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/admin/emails/deliveries/by-message-id/${encodeURIComponent(providerMessageId)}/view`)
      .then((r) => { if (!cancelled) setDelivery(r.data); })
      .catch((e) => {
        if (!cancelled) {
          setError(formatApiError(e.response?.data?.detail) || e.message);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [providerMessageId]);

  // ESC-to-close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const srcdoc = useMemo(() => {
    if (!delivery?.rendered_html) return "";
    return buildSrcdoc(delivery.rendered_html, showImages);
  }, [delivery, showImages]);

  const statusTone = STATUS_TONE[delivery?.status] || STATUS_TONE.skipped_disabled;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-stretch md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="email-viewer-backdrop"
    >
      <div
        className="relative bg-white w-full md:max-w-4xl md:rounded-2xl border-2 border-[var(--ink)] shadow-[8px_8px_0_var(--ink)] flex flex-col overflow-hidden max-h-full"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-viewer-title"
        data-testid="email-viewer-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)]/60">
          <div className="flex items-center gap-2 min-w-0">
            <Mail size={16} className="text-[var(--ink)]" />
            <h3 id="email-viewer-title" className="font-display text-lg text-[var(--ink)] truncate">
              View Email
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="email-viewer-close"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full border-2 border-[var(--ink)] hover:bg-white"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="p-10 text-center text-[var(--smoke)]" data-testid="email-viewer-loading">
              <Loader2 className="animate-spin inline mr-2" size={16} /> Loading email…
            </div>
          )}
          {error && (
            <div className="p-6 text-red-800 bg-red-50 border-l-4 border-red-700"
                 data-testid="email-viewer-error">
              {error}
            </div>
          )}
          {delivery && (
            <>
              {/* Metadata block */}
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm px-5 py-4 border-b border-neutral-200 bg-white"
                  data-testid="email-viewer-meta">
                <dt className="text-[var(--smoke)]">Recipient</dt>
                <dd className="font-mono break-all" data-testid="email-viewer-recipient">
                  {delivery.recipient_name ? `${delivery.recipient_name} <${delivery.to}>` : delivery.to}
                </dd>
                <dt className="text-[var(--smoke)]">Subject</dt>
                <dd className="font-semibold" data-testid="email-viewer-subject">
                  {delivery.subject || <span className="italic opacity-70">(no subject)</span>}
                </dd>
                <dt className="text-[var(--smoke)]">Sent</dt>
                <dd data-testid="email-viewer-sent-at">
                  {detailLong(delivery.completed_at || delivery.created_at) || "—"}
                </dd>
                <dt className="text-[var(--smoke)]">Delivery status</dt>
                <dd>
                  <span className={`inline-flex items-center gap-1 border-2 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${statusTone}`}
                        data-testid="email-viewer-status">
                    {delivery.status || "unknown"}
                  </span>
                  {delivery.error && (
                    <span className="ml-2 text-xs italic text-[var(--smoke)] break-all">{delivery.error}</span>
                  )}
                </dd>
                <dt className="text-[var(--smoke)]">Template</dt>
                <dd className="font-mono text-xs" data-testid="email-viewer-template">
                  {delivery.template_id || "unknown"}
                  {delivery.template_version && (
                    <span className="opacity-60"> · v{delivery.template_version}</span>
                  )}
                </dd>
                {delivery.provider_message_id && (
                  <>
                    <dt className="text-[var(--smoke)]">Provider message ID</dt>
                    <dd className="font-mono text-xs break-all flex items-center gap-2">
                      <span data-testid="email-viewer-message-id">{delivery.provider_message_id}</span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(delivery.provider_message_id)}
                        aria-label="Copy provider message ID"
                        className="inline-flex items-center justify-center w-6 h-6 rounded border border-neutral-400 hover:bg-neutral-100"
                      >
                        <ClipboardCopy size={11} />
                      </button>
                    </dd>
                  </>
                )}
              </dl>

              {/* Snapshot content */}
              {!delivery.has_snapshot ? (
                <div
                  className="p-8 text-center bg-neutral-50"
                  data-testid="email-viewer-no-snapshot"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-neutral-200 mb-3">
                    <EyeOff size={20} className="text-neutral-600" />
                  </div>
                  <p className="text-sm text-neutral-700 max-w-md mx-auto">
                    The exact email content was not stored for this historical
                    notification. Only the metadata above (subject, recipient,
                    status, template) is available. Emails sent from this
                    point forward include a full immutable snapshot.
                  </p>
                </div>
              ) : (
                <>
                  {/* Toolbar */}
                  <div className="flex items-center justify-between gap-3 px-5 py-2 border-b border-neutral-200 bg-neutral-50">
                    <div className="text-xs text-[var(--smoke)]">
                      Read-only preview · scripts and remote resources blocked
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowText((v) => !v)}
                        className={`inline-flex items-center gap-1 text-xs font-bold border-2 border-[var(--ink)] rounded-full px-2.5 py-1 hover:bg-white ${showText ? "bg-[var(--panini-yellow)]" : "bg-white"}`}
                        data-testid="email-viewer-toggle-text"
                      >
                        {showText ? "HTML view" : "Text view"}
                      </button>
                      {!showText && (
                        <label className="inline-flex items-center gap-2 text-xs font-bold border-2 border-[var(--ink)] rounded-full px-2.5 py-1 cursor-pointer bg-white hover:bg-[var(--panini-yellow-soft)]"
                               data-testid="email-viewer-images-toggle">
                          <input
                            type="checkbox"
                            checked={showImages}
                            onChange={(e) => setShowImages(e.target.checked)}
                            className="w-3.5 h-3.5"
                          />
                          <ImageIcon size={12} />
                          Show images
                        </label>
                      )}
                    </div>
                  </div>
                  {showText ? (
                    <pre
                      className="p-5 text-sm whitespace-pre-wrap font-mono text-[var(--ink)] bg-white overflow-auto max-h-[65vh]"
                      data-testid="email-viewer-text"
                    >
                      {delivery.rendered_text || "(no plain text)"}
                    </pre>
                  ) : (
                    <iframe
                      key={showImages ? "with-images" : "no-images"}
                      title="Email preview"
                      sandbox=""
                      srcDoc={srcdoc}
                      className="w-full min-h-[400px] md:min-h-[520px] bg-white"
                      data-testid="email-viewer-iframe"
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
